import { type ReactElement, useCallback, useEffect, useMemo, useRef } from 'react'
import { Search, Upload, X } from 'lucide-react'
import { useDropzone, type FileRejection } from 'react-dropzone'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'
import { useInvoices } from '@/features/invoices/hooks/useInvoices'
import type { Invoice } from '@/features/invoices/schemas/invoicesSchema'
import { useInfiniteScrollTrigger } from '../hooks/useInfiniteScrollTrigger'
import { useStableOrder } from '../hooks/useStableOrder'
import { useViewportScrollAreaHeight } from '../hooks/useViewportScrollAreaHeight'
import { matchesAmount, normalizeAmountQuery } from '../utils/amountSearch'
import { ColumnMonthNav } from './ColumnMonthNav'
import { InvoiceCard } from './InvoiceCard'

const PAGE_SIZE = 50

interface InvoicesColumnProps {
  year: number
  monthIndex: number
  onMonthChange: (year: number, monthIndex: number) => void
  searchQuery: string
  onSearchChange: (query: string) => void
  isDualColumn: boolean
  canDragInvoices: boolean
  showSatisfactionStatus: boolean
  recentlyLinkedInvoiceUuid?: string | null
  onEditInvoice: (invoice: Invoice) => void
  onUploadInvoiceFiles: (files: File[]) => void
  className?: string
}

export function InvoicesColumn({
  year,
  monthIndex,
  onMonthChange,
  searchQuery,
  onSearchChange,
  isDualColumn,
  canDragInvoices,
  showSatisfactionStatus,
  recentlyLinkedInvoiceUuid,
  onEditInvoice,
  onUploadInvoiceFiles,
  className,
}: InvoicesColumnProps): ReactElement {
  const { t } = useTranslation('features-invoices')
  const {
    data,
    isLoading,
    isError,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInvoices(year, monthIndex, PAGE_SIZE)

  const invoices = useMemo(
    () => data?.pages.flatMap(page => page.content) ?? [],
    [data],
  )
  const orderedInvoices = useStableOrder(invoices, invoice => invoice.uuid, `${year}-${monthIndex}`)

  const filteredInvoices = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (query.length === 0) return orderedInvoices
    const amountQuery = normalizeAmountQuery(searchQuery)
    return orderedInvoices.filter(invoice => {
      if (
        invoice.invoiceNumber.toLowerCase().includes(query) ||
        invoice.counterparty.toLowerCase().includes(query)
      ) {
        return true
      }
      if (
        amountQuery &&
        (matchesAmount(invoice.grossAmount, amountQuery) ||
          matchesAmount(invoice.netAmount, amountQuery))
      ) {
        return true
      }
      return false
    })
  }, [orderedInvoices, searchQuery])

  const handleInvoiceFileDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]): void => {
      if (rejectedFiles.length > 0) {
        const message = rejectedFiles[0]?.errors?.[0]?.message ?? 'Invalid file'
        toast.error(message)
      }
      if (acceptedFiles.length > 0) onUploadInvoiceFiles(acceptedFiles)
    },
    [onUploadInvoiceFiles],
  )

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: handleInvoiceFileDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    multiple: true,
    noClick: true,
    noKeyboard: true,
  })

  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const scrollAreaHeight = useViewportScrollAreaHeight(scrollAreaRef)

  useInfiniteScrollTrigger({
    scrollRef: scrollAreaRef,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    recomputeKey: `${scrollAreaHeight ?? 0}:${filteredInvoices.length}`,
  })

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented || event.key !== 'Escape') return
      if (searchQuery.length === 0) return
      onSearchChange('')
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [searchQuery, onSearchChange])

  return (
    <div className={cn('min-w-0', className)}>
      <ColumnMonthNav
        label={t('title')}
        year={year}
        monthIndex={monthIndex}
        onChange={onMonthChange}
      />

      <div {...getRootProps({ className: 'relative mt-5' })}>
        <input {...getInputProps()} />
        {isDragActive ? (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-xl border-[3px] border-dashed border-primary/75 bg-accent/35 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.18),0_0_0_2px_rgba(99,102,241,0.18)]">
            <div className="rounded-lg border border-primary/60 bg-card/92 px-4 py-2 text-sm font-semibold text-foreground shadow-md">
              Drop invoice(s) anywhere in this column
            </div>
          </div>
        ) : null}

        <div className="flex items-center gap-2">
          <div className="relative min-w-0 flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/70" />
            <Input
              type="text"
              value={searchQuery}
              onChange={event => onSearchChange(event.target.value)}
              placeholder={t('search.placeholder', {
                defaultValue: 'Search invoice, counterparty, or amount...',
              })}
              className="h-11 w-full rounded-lg border-border/70 bg-card pl-9 pr-9 focus-visible:border-foreground/35 focus-visible:ring-0"
            />
            {searchQuery.length > 0 ? (
              <button
                type="button"
                onClick={() => onSearchChange('')}
                aria-label={t('search.clearAria', { defaultValue: 'Clear search' })}
                className="absolute right-2 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground/70 transition-colors hover:bg-muted/20 hover:text-muted-foreground"
              >
                <X className="size-3.5" />
              </button>
            ) : null}
          </div>

          <button
            type="button"
            onClick={event => {
              event.stopPropagation()
              open()
            }}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border/70 bg-card px-0 text-sm text-muted-foreground transition-all duration-200 hover:border-primary/40 sm:w-[230px] sm:px-3"
          >
            <Upload className="h-4 w-4" />
            <span className="hidden truncate sm:inline">Drop invoice</span>
          </button>
        </div>

        <div
          ref={scrollAreaRef}
          data-kanban-scroll-area="true"
          className={cn(
            'mt-3 space-y-3 overflow-y-auto overscroll-contain pr-1 scrollbar-subtle transition-colors',
            isDragActive && 'rounded-xl bg-accent/10',
          )}
          style={scrollAreaHeight ? { height: `${scrollAreaHeight}px` } : undefined}
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner className="size-5" />
            </div>
          ) : isError ? (
            <div className="py-12 text-center text-sm text-destructive">
              {error instanceof Error ? error.message : 'Failed to load invoices'}
            </div>
          ) : orderedInvoices.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No invoices this month
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              {t('list.noSearchResults', { defaultValue: 'No matching invoices found.' })}
            </div>
          ) : (
            filteredInvoices.map((invoice, index) => (
              <InvoiceCard
                key={invoice.uuid}
                index={index + 1}
                invoice={invoice}
                isDualColumn={isDualColumn}
                canDrag={canDragInvoices}
                showSatisfactionStatus={showSatisfactionStatus}
                recentlyLinked={recentlyLinkedInvoiceUuid === invoice.uuid}
                onEditInvoice={onEditInvoice}
              />
            ))
          )}

          {isFetchingNextPage && (
            <div className="flex justify-center py-2">
              <Spinner className="size-4" />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
